import { BuildClass, DateObj, DateTimeObj, Do, clamp, } from '../../../universal';
import { React, _ } from '../../lib';
import { ConditionalObject, useRSInstance, useStateSync, } from './meta-types';
import { ModalAnchored } from './modal';
import { stubFocusable, stubInput, stubModal } from './stubs';
import { TextboxGeneric } from './textbox';
const defaultMinYear = 1900;
const defaultMaxYear = 2100;
const DateBoxComponentInner = (props, ref) => {
    const rs = useRSInstance({
        props: props,
        refs: {
            input: React.useRef(stubFocusable(stubInput)),
            modal: React.useRef(stubModal),
        },
        defaultState: getDefaultStateFromProps,
        actionToDelta: getPartialStateFromAction,
    });
    useStateSync({
        stateVal: rs.state.value,
        propVal: rs.props.value,
        setState: v => {
            const value = parseValue(props, v);
            rs.dispatch([
                Action.SetState,
                {
                    value: value,
                    valueRaw: getValueRaw(props, value),
                    selectionPath: getSelectionPath(value),
                },
            ]);
        },
        setProp: v => {
            props.onUpdate(v);
        },
        compareFn: (pv, sv) => parseValue(props, pv) == sv,
    });
    useStateSync({
        stateVal: rs.state.zoomLevel,
        propVal: getDefaultZoom(rs.props),
        setState: v => {
            rs.dispatch([Action.UpdateZoom, { zoom: v }]);
        },
        setProp: () => { },
    });
    const onUpdate = React.useCallback((v) => {
        onTextChanged(rs, v);
    }, [rs]);
    const onFocus = React.useCallback((ev) => {
        onTxtFocus(rs);
        props.onFocus?.(ev);
    }, [rs, props.onFocus]);
    const onBlur = React.useCallback((ev) => {
        onTxtBlur(rs);
        props.onBlur?.(ev);
    }, [rs, props.onBlur]);
    const onKeyDownInner = React.useCallback((e) => {
        onKeyDown(rs, e);
        props.onKeyDown?.(e);
    }, [rs, props.onKeyDown]);
    const onClickInner = React.useCallback(() => {
        onClick(rs);
    }, [rs]);
    const onWheelModal = React.useCallback((e) => {
        if (e.deltaY < 0) {
            moveNav(rs, -1);
        }
        else if (e.deltaY > 0) {
            moveNav(rs, +1);
        }
    }, [rs]);
    const placeholder = React.useMemo(() => {
        if (props.placeholder != null) {
            return props.placeholder;
        }
        else if (!props.includeTime) {
            return 'dd/mm/yyyy';
        }
        else if (!(props.includeDate ?? true)) {
            return 'hh:mm';
        }
        return 'dd/mm/yyyy, hh:mm';
    }, [props.placeholder, props.includeDate, props.includeTime]);
    React.useImperativeHandle(ref, () => ({
        getModal: () => rs.refs.modal.current?.getElement(),
        getElement: () => rs.refs.input.current?.getElement() ?? stubInput,
        focus: () => {
            Focus(rs);
        },
        select: () => {
            Select(rs);
        },
    }));
    const identity = React.useCallback((x) => x, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(TextboxGeneric, { ref: rs.refs.input, className: BuildClass({
                'ui5-datebox': true,
                [BuildClass(props.className)]: true,
                unsaved: rs.state.isEditing,
                focused: rs.state.isFocused,
                disabled: Boolean(props.disabled),
            }), value: rs.state.valueRaw, onUpdate: onUpdate, title: props.title, fixOnBlur: identity, serialize: identity, deserialize: identity, disabled: props.disabled, autoFocus: props.autoFocus, readOnly: props.readOnly, placeholder: placeholder, onFocus: onFocus, onBlur: onBlur, onKeyDown: onKeyDownInner, onClick: onClickInner, selectOnClick: false, style: props.style }),
        React.createElement(ModalAnchored, { ref: rs.refs.modal, hidden: props.readOnly || !rs.state.isOpen, mountElement: rs.refs.input.current?.getElement() ?? stubInput, width: 270, maxHeight: 250 },
            React.createElement("div", { className: "ui5-datebox-widget", onWheel: onWheelModal },
                ConditionalObject(rs.state.isOpen, () => buildWidgetToolbar(rs)),
                ConditionalObject(rs.state.isOpen, () => buildWidgetGrid(rs))))));
};
const buildWidgetToolbar = (rs) => (React.createElement("div", { key: "toolbar", className: "toolbar noselect" },
    React.createElement("div", { className: BuildClass({
            lhs: true,
            side: true,
            invisible: rs.state.zoomLevel === 1,
        }), onMouseDown: stopProp, onClick: () => {
            moveNav(rs, -1);
        } }, "\u2191"),
    React.createElement("div", { className: "home icon", title: "Jump to today", onMouseDown: stopProp, onClick: () => {
            rs.dispatch([
                Action.SetState,
                {
                    selectionPath: getSelectionPath(DateTimeObj.now(), rs.state),
                    zoomLevel: (rs.props.includeDate ?? true) ? 4 : 5,
                },
            ]);
        } },
        React.createElement("img", { src: "/static/img/svg/home.svg" })),
    React.createElement("div", { className: BuildClass({
            title: true,
            'non-clickable': rs.state.zoomLevel === 1,
        }), onMouseDown: stopProp, onClick: () => {
            moveNesting(rs, -1);
        } }, Do(() => {
        const P = rs.state.selectionPath;
        const dt = DateTimeObj.create({
            year: P.decades * 10 + P.years,
            month: P.months ?? 1,
            day: P.days ?? 1,
            hour: P.hours ?? 1,
            min: 0,
        });
        switch (rs.state.zoomLevel) {
            case 1:
                return '';
            case 2:
                return `${P.decades}0s`;
            case 3:
                return dt.fmt('yyyy');
            case 4:
                return dt.fmt('LLLL, yyyy');
            case 5:
                return dt.fmt('dd/MM/yyyy');
            case 6:
                const h = dt.fmt('HH');
                const d = dt.fmt('dd/MM/yy');
                return `${h}:xx, ${d}`;
        }
    })),
    React.createElement("div", { className: BuildClass({
            icon: true,
            target: true,
            nohover: rs.state.value == null,
        }), title: "Jump to current value", onMouseDown: stopProp, onClick: () => {
            if (rs.state.value != null) {
                rs.dispatch([
                    Action.SetState,
                    {
                        selectionPath: getSelectionPath(rs.state.value, rs.state),
                    },
                ]);
            }
        } },
        React.createElement("img", { src: "/static/img/svg/target.svg" })),
    React.createElement("div", { className: BuildClass({
            rhs: true,
            side: true,
            invisible: rs.state.zoomLevel === 1,
        }), onMouseDown: stopProp, onClick: () => {
            moveNav(rs, +1);
        } }, "\u2193")));
const buildWidgetGrid = (rs) => {
    switch (rs.state.zoomLevel) {
        case 1:
            return buildWidgetGridDecades(rs);
        case 2:
            return buildWidgetGridYears(rs);
        case 3:
            return buildWidgetGridMonths(rs);
        case 4:
            return buildWidgetGridDays(rs);
        case 5:
            return buildWidgetGridHours(rs);
        case 6:
            return buildWidgetGridMinutes(rs);
    }
};
const buildGridOfOptions = (rs, param) => {
    const today = param.getID(DateTimeObj.now());
    const current = param.getID(rs.state.value);
    const groupID = param.getGroupID?.(param.startDateGroup ?? param.startDate);
    return (React.createElement("div", { className: "calendar-view" },
        ConditionalObject(Boolean(param.headingRow), param.headingRow),
        ..._.times(param.rowCount, y => (React.createElement("div", { key: y, className: BuildClass({
                [BuildClass(param.className)]: true,
                'calendar-row': true,
            }) }, _.times(param.colCount, x => {
            const dDate = param.getCellDate(param.startDate, x, y);
            const dID = param.getID(dDate);
            return (React.createElement("span", { key: x, className: BuildClass({
                    day: true,
                    cell: true,
                    oog: Boolean(groupID && groupID !== param.getGroupID?.(dDate)),
                    today: dID === today,
                    current: dID === current,
                }), title: (param.getCellTitle ?? param.getCellText)(dDate), onMouseDown: stopProp, onClick: e => {
                    e.preventDefault();
                    e.stopPropagation();
                    (param.onClick ?? _.noop)(dDate);
                } }, param.getCellText(dDate)));
        }))))));
};
const buildWidgetGridDecades = (rs) => buildGridOfOptions(rs, {
    startDate: DateTimeObj.create({
        year: 1900,
        month: 1,
        day: 1,
    }),
    rowCount: 5,
    colCount: 4,
    getID: x => String(Math.floor((x?.lx.year ?? -1) / 10)),
    getCellDate: (start, x, y) => start.add((y * 4 + x) * 10, 'years'),
    getCellText: x => {
        const decade = Math.floor(x.lx.year / 10);
        return `${decade}0s`;
    },
    getGroupID: null,
    onClick: d => {
        rs.dispatch([
            Action.SelectItem,
            {
                selectionPath: Do(() => {
                    const p = _.clone(rs.state.selectionPath);
                    p.decades = Math.floor(d.lx.year / 10);
                    return p;
                }),
            },
        ]);
    },
});
const buildWidgetGridYears = (rs) => buildGridOfOptions(rs, {
    startDate: DateTimeObj.create({
        year: rs.state.selectionPath.decades * 10,
        month: 1,
        day: 1,
    }),
    rowCount: 5,
    colCount: 4,
    getID: x => String(x?.lx.year ?? -1),
    getCellDate: (start, x, y) => start.add((y - 1) * 4 + x, 'years'),
    getCellText: x => x.fmt('yyyy'),
    getGroupID: x => String(Math.floor(x.lx.year / 10)),
    onClick: d => {
        rs.dispatch([
            Action.SelectItem,
            {
                selectionPath: Do(() => {
                    const p = _.clone(rs.state.selectionPath);
                    p.decades = Math.floor(d.lx.year / 10);
                    p.years = d.lx.year % 10;
                    return p;
                }),
            },
        ]);
    },
});
const buildWidgetGridMonths = (rs) => buildGridOfOptions(rs, {
    startDate: DateTimeObj.create({
        year: rs.state.selectionPath.decades * 10 + rs.state.selectionPath.years,
        month: 1,
        day: 1,
    }),
    rowCount: 5,
    colCount: 4,
    getID: x => x?.fmt('MM/yyyy') ?? '',
    getCellDate: (start, x, y) => start.add((y - 1) * 4 + x, 'months'),
    getCellText: x => x.fmt('MMM'),
    getCellTitle: x => x.fmt('MMMM, yyyy'),
    getGroupID: x => String(x.lx.year),
    onClick: d => {
        rs.dispatch([
            Action.SelectItem,
            {
                selectionPath: Do(() => {
                    const p = _.clone(rs.state.selectionPath);
                    p.decades = Math.floor(d.lx.year / 10);
                    p.years = d.lx.year % 10;
                    p.months = d.lx.month;
                    return p;
                }),
            },
        ]);
    },
});
const buildWidgetGridDays = (rs) => buildGridOfOptions(rs, {
    className: 'days',
    headingRow: (React.createElement("div", { key: "heading", className: "heading-row calendar-row gridRow days" }, ['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((txt, idx) => (React.createElement("span", { key: idx, className: "cell" }, txt))))),
    startDate: Do(() => {
        let date_ = DateTimeObj.create({
            year: rs.state.selectionPath.decades * 10 + rs.state.selectionPath.years,
            month: rs.state.selectionPath.months,
            day: 1,
        });
        if (!date_.lx.isValid) {
            console.error('Invalid date', date_);
            return DateTimeObj.now();
        }
        while (date_.lx.isValid && date_.lx.weekday !== 1) {
            date_ = date_.subtract(1, 'days');
        }
        return date_;
    }),
    startDateGroup: DateTimeObj.create({
        year: rs.state.selectionPath.decades * 10 + rs.state.selectionPath.years,
        month: rs.state.selectionPath.months,
        day: 1,
    }),
    rowCount: 6,
    colCount: 7,
    getID: x => x?.fmt('dd/MM/yyyy') ?? '',
    getCellDate: (start, x, y) => start.add(y * 7 + x, 'days'),
    getCellText: x => String(x.lx.day),
    getCellTitle: x => x.fmt('EEEE, dd/MM/yyyy'),
    getGroupID: x => x.fmt('MM/yyyy'),
    onClick: d => {
        if (!rs.props.includeTime) {
            updateValue(rs, d);
            rs.dispatch([Action.SetState, { isOpen: false }]);
            return;
        }
        rs.dispatch([
            Action.SelectItem,
            {
                selectionPath: Do(() => {
                    const p = _.clone(rs.state.selectionPath);
                    p.decades = Math.floor(d.lx.year / 10);
                    p.years = d.lx.year % 10;
                    p.months = d.lx.month;
                    p.days = d.lx.day;
                    return p;
                }),
            },
        ]);
    },
});
const buildCircleOfOptions = (rs, param) => {
    const today = param.getID(DateTimeObj.now());
    const current = param.getID(rs.state.value);
    return (React.createElement("div", { key: param.key, className: BuildClass({
            [param.cl]: true,
            circle: true,
        }) }, _.map(param.segments, (segment, index) => {
        const dDate = param.getSegmentDT(param.startDate, segment.value);
        const dID = param.getID(dDate);
        return (React.createElement("div", { key: index, className: BuildClass({
                segment: true,
                today: dID === today,
                current: dID === current,
            }), style: {
                ...segment.style,
                transform: Do(() => {
                    const degs = (index * 360) / param.segments.length;
                    const existing = segment.style?.transform ?? '';
                    return `${existing} rotate(${degs}deg)`;
                }),
            }, onMouseDown: stopProp, onClick: e => {
                e.preventDefault();
                e.stopPropagation();
                segment.onClick(segment.value);
            } },
            React.createElement("span", { className: "txt", style: {
                    transform: Do(() => {
                        const degs = (index * 360) / param.segments.length;
                        return `rotate(-${degs}deg)`;
                    }),
                } }, segment.text)));
    })));
};
const buildWidgetGridHours = (rs) => (React.createElement("div", { className: "hour-picker noselect" },
    buildCircleOfOptions(rs, {
        cl: 'outer',
        key: 'outer',
        startDate: DateTimeObj.create({
            year: rs.state.selectionPath.decades * 10 + rs.state.selectionPath.years,
            month: rs.state.selectionPath.months,
            day: rs.state.selectionPath.days,
            hour: 0,
            min: 0,
        }),
        getID: x => x?.fmt('HH dd/MM/yyyy') ?? '',
        getSegmentDT: (start, delta) => start.add(delta, 'hours'),
        segments: _.times(12, x => ({
            value: x,
            text: x < 10 ? `0${x}` : String(x),
            onClick: v => {
                rs.dispatch([
                    Action.SelectItem,
                    {
                        selectionPath: Do(() => {
                            const p = _.clone(rs.state.selectionPath);
                            p.hours = v;
                            return p;
                        }),
                    },
                ]);
            },
        })),
    }),
    buildCircleOfOptions(rs, {
        cl: 'inner',
        key: 'inner',
        startDate: DateTimeObj.create({
            year: rs.state.selectionPath.decades * 10 + rs.state.selectionPath.years,
            month: rs.state.selectionPath.months,
            day: rs.state.selectionPath.days,
            hour: 0,
            min: 0,
        }),
        getID: x => x?.fmt('HH dd/MM/yyyy') ?? '',
        getSegmentDT: (start, delta) => start.add(delta, 'hours'),
        segments: _.times(12, x => ({
            value: x + 12,
            text: String(x + 12),
            onClick: v => {
                rs.dispatch([
                    Action.SelectItem,
                    {
                        selectionPath: Do(() => {
                            const p = _.clone(rs.state.selectionPath);
                            p.hours = v;
                            return p;
                        }),
                    },
                ]);
            },
        })),
    }),
    React.createElement("div", { className: "dot" })));
const buildWidgetGridMinutes = (rs) => (React.createElement("div", { className: "hour-picker noselect" },
    buildCircleOfOptions(rs, {
        cl: 'outer',
        key: 'outer',
        startDate: DateTimeObj.create({
            year: rs.state.selectionPath.decades * 10 + rs.state.selectionPath.years,
            month: rs.state.selectionPath.months,
            day: rs.state.selectionPath.days,
            hour: rs.state.selectionPath.hours,
            min: 0,
        }),
        getID: x => {
            if (x == null) {
                return '';
            }
            const dt = x.fmt('dd/MM/yyyy HH') ?? '';
            const m = Math.floor(+x.fmt('mm') / 5);
            return `${dt}${m}`;
        },
        getSegmentDT: (start, delta) => start.add(delta, 'minutes'),
        segments: _.times(12, x => ({
            value: x * 5,
            text: Do(() => {
                const v = String(x * 5);
                if (v.length < 2) {
                    return `0${v}`;
                }
                return v;
            }),
            onClick: v => {
                const p = _.clone(rs.state.selectionPath);
                p.minutes = v;
                const path = clampSelectedPath(p, rs.state.zoomLevel);
                updateValue(rs, getValueFromSelectionPath(rs, path));
                rs.dispatch([Action.SetState, { isOpen: false }]);
            },
        })),
    }),
    React.createElement("div", { className: "dot" })));
const updateValue = (rs, v) => {
    rs.dispatch([
        Action.SetState,
        {
            value: v,
            isEditing: false,
            valueRaw: getValueRaw(rs.props, v),
            selectionPath: getSelectionPath(v, rs.state),
        },
    ]);
};
const parseValue = (props, val) => {
    if (!val) {
        return null;
    }
    const fmt = getValueFormat(props);
    const dtVal = Do(() => {
        if (val instanceof DateTimeObj) {
            return val;
        }
        if (val instanceof DateObj) {
            return DateTimeObj.parse(val.raw);
        }
        if (val instanceof Date) {
            return DateTimeObj.parse(val.toISOString());
        }
        return DateTimeObj.parse(val, fmt);
    });
    if (dtVal != null) {
        return dtVal;
    }
    console.error(`Could not parse given \`DateBox\` value: '${val}' with format '${fmt}'`);
    return null;
};
const getValueFormat = (props) => {
    if (!props.includeTime) {
        return 'yyyy-MM-dd';
    }
    else if (!(props.includeDate ?? true)) {
        return 'HH:mm';
    }
    return 'yyyy-MM-dd HH:mm:ss';
};
const onClick = (rs) => {
    rs.dispatch([Action.SetState, { isOpen: true }]);
};
const onTxtFocus = (rs) => {
    rs.dispatch([
        Action.SetState,
        {
            isFocused: true,
            isOpen: true,
            zoomLevel: getDefaultZoom(rs.props),
        },
    ]);
};
const onTxtBlur = (rs) => {
    rs.dispatch([
        Action.SetState,
        {
            isFocused: false,
            isOpen: false,
        },
    ]);
    if (rs.state.isEditing) {
        attemptToCommitEnteredValue(rs);
    }
};
const getValueRaw = (props, v) => {
    if (v == null) {
        return '';
    }
    else if (props.fmt != null) {
        return v.fmt(props.fmt);
    }
    else if (!props.includeTime) {
        return v.fmt('dd/MM/yyyy');
    }
    else if (!(props.includeDate ?? true)) {
        return v.fmt('HH:mm');
    }
    return v.fmt('dd/MM/yyyy, HH:mm');
};
const onTextChanged = (rs, value) => {
    rs.dispatch([
        Action.SetState,
        {
            isEditing: true,
            valueRaw: value,
        },
    ]);
    if (!value) {
        updateValue(rs, null);
        return;
    }
    const fmt = Do(() => {
        if (rs.props.fmt != null) {
            return rs.props.fmt;
        }
        else if (!rs.props.includeTime) {
            return 'DD/MM/YYYY';
        }
        else if (!(rs.props.includeDate ?? true)) {
            return 'HH:mm';
        }
        return 'DD/MM/YYYY, HH:mm';
    });
    const attempted_value = DateTimeObj.parse(value, fmt);
    if (attempted_value.lx.isValid) {
        updateValue(rs, attempted_value);
        return;
    }
};
const attemptToCommitEnteredValue = (rs) => {
    const v = attemptToParseEnteredValue(rs);
    if (v !== false) {
        updateValue(rs, v);
        return;
    }
    rs.dispatch([
        Action.SetState,
        {
            isEditing: false,
            valueRaw: getValueRaw(rs.props, rs.state.value),
        },
    ]);
};
const attemptToParseEnteredValue = (rs) => {
    let fmts;
    if (!rs.props.includeTime) {
        fmts = [
            'ddMMyyyy',
            'yyyyMMdd',
            'ddMMyy',
            'yyMMdd',
            'dd/MM/yyyy',
            'yyyy-MM-dd',
            'dd/MM/yy',
            'd/M/yy',
            'd/M/y',
            'yy-M-d',
            'y-M-d',
            'dd.MM.yyyy',
            'yyyy.MM.dd',
            'd.M.yy',
            'd.M.y',
            'yy.M.d',
            'y.M.d',
            'dMy',
            'yMd',
        ];
    }
    else if (!(rs.props.includeDate ?? true)) {
        fmts = [
            'HH:mm',
            'Hmm',
            'H.mm',
            'H:mm',
            'H-mm',
            'H mm',
            'HH:mm:ss',
            'Hmmss',
            'H.mm.ss',
            'H:mm:ss',
            'H-mm-ss',
            'H mm ss',
        ];
    }
    else {
        console.error('Must either `includeTime` or `includeDate`');
        return false;
    }
    let match;
    _.forEach(fmts, fmt => {
        const v = DateTimeObj.parse(rs.state.valueRaw, fmt);
        if (v.lx.isValid) {
            match = v;
            return false;
        }
        return true;
    });
    const maxYear = rs.props.maxYear ?? defaultMaxYear;
    const minYear = rs.props.minYear ?? defaultMinYear;
    if (match && (match.lx.year > maxYear || match.lx.year < minYear)) {
        match = false;
    }
    return match;
};
const onKeyDown = (rs, e) => {
    const delta = e.ctrlKey || e.shiftKey ? 7 : 1;
    switch (e.keyCode) {
        case 38:
            stopProp(e);
            arrowNav(rs, -delta);
            break;
        case 40:
            stopProp(e);
            arrowNav(rs, delta);
            break;
        case 33:
            stopProp(e);
            arrowNav(rs, -7 * delta);
            break;
        case 34:
            arrowNav(rs, 7 * delta);
            break;
        case 36:
            stopProp(e);
            arrowNav(rs, -28 * delta);
            break;
        case 35:
            stopProp(e);
            arrowNav(rs, 28 * delta);
            break;
        case 13:
            attemptToCommitEnteredValue(rs);
            rs.dispatch([Action.SetState, { isOpen: false }]);
            break;
        case 27:
            rs.dispatch([
                Action.SetState,
                {
                    isOpen: false,
                    isEditing: false,
                    valueRaw: getValueRaw(rs.props, rs.state.value),
                },
            ]);
            break;
        default:
            return;
    }
    stopProp(e);
};
const arrowNav = (rs, delta) => {
    if (!rs.props.disableArrowKeys) {
        const newZoomLevel = clamp(getDefaultZoom(rs.props) + 1, 1, 6);
        moveNav(rs, delta, newZoomLevel, selectionPath => {
            updateValue(rs, getValueFromSelectionPath(rs, selectionPath));
        });
    }
};
const getDefaultZoom = (props) => props.defaultZoom ?? ((props.includeDate ?? true) ? 4 : 5);
const getSelectionPath = (value, state) => {
    const val = value ?? state?.value ?? DateTimeObj.now();
    return {
        decades: Math.floor(val.lx.year / 10),
        years: val.lx.year % 10,
        months: val.lx.month,
        days: val.lx.day,
        hours: val.lx.hour,
        minutes: Math.round(val.lx.minute / 12),
    };
};
const getValueFromSelectionPath = (rs, p) => {
    p = p ?? rs.state.selectionPath;
    return DateTimeObj.create({
        year: p.decades * 10 + p.years,
        month: p.months,
        day: p.days,
        hour: p.hours,
        min: p.minutes,
        sec: 0,
    });
};
const moveNav = (rs, delta, zoomLevel, cb) => {
    zoomLevel = zoomLevel ?? rs.state.zoomLevel;
    const path = _.clone(rs.state.selectionPath);
    if (zoomLevel <= 2) {
        path.decades += delta;
    }
    else if (zoomLevel === 3) {
        path.years += delta;
    }
    else if (zoomLevel === 4) {
        path.months += delta;
    }
    else if (zoomLevel === 5) {
        path.days += delta;
    }
    else if (zoomLevel === 6) {
        path.hours += delta;
    }
    rs.dispatch([Action.UpdateSelectionPath, { path }]);
    cb?.(clampSelectedPath(path, zoomLevel));
};
const clampSelectedPath = (path, zoomLevel) => {
    zoomLevel = clamp(zoomLevel, 1, 6);
    while (path.minutes < 0) {
        path.minutes += 60;
        path.hours -= 1;
    }
    while (path.minutes > 59) {
        path.minutes -= 60;
        path.hours += 1;
    }
    while (path.hours < 0) {
        path.hours += 24;
        path.days -= 1;
    }
    while (path.hours > 23) {
        path.hours -= 24;
        path.days += 1;
    }
    while (path.months < 1) {
        path.months += 12;
        path.years -= 1;
    }
    while (path.months > 12) {
        path.months -= 12;
        path.years += 1;
    }
    if (zoomLevel >= 4) {
        const d = DateTimeObj.create({
            year: path.decades * 10 + path.years,
            month: path.months,
            day: 1,
        }).add(path.days - 1, 'days');
        path.decades = Math.floor(d.lx.year / 10);
        path.years = d.lx.year % 10;
        path.months = d.lx.month;
        path.days = d.lx.day;
    }
    while (path.years < 0) {
        path.years += 10;
        path.decades -= 1;
    }
    while (path.years > 9) {
        path.years -= 10;
        path.decades += 1;
    }
    path.decades = clamp(path.decades, 180, 220);
    return path;
};
const moveNesting = (rs, delta) => {
    const zoom = rs.state.zoomLevel + delta;
    const zoomClamped = clamp(zoom, 1, 6);
    rs.dispatch([Action.UpdateZoom, { zoom: zoomClamped }]);
};
const Focus = (rs) => {
    const starting_is_open = rs.state.isOpen;
    rs.refs.input.current?.focus();
    if (!starting_is_open) {
        rs.dispatch([Action.SetState, { isOpen: starting_is_open }]);
    }
};
const Select = (rs) => {
    rs.refs.input.current?.select();
};
const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
};
var Action;
(function (Action) {
    Action[Action["SetState"] = 0] = "SetState";
    Action[Action["ResetValueFromProps"] = 1] = "ResetValueFromProps";
    Action[Action["UpdateSelectionPath"] = 2] = "UpdateSelectionPath";
    Action[Action["UpdateZoom"] = 3] = "UpdateZoom";
    Action[Action["SelectItem"] = 4] = "SelectItem";
})(Action || (Action = {}));
const getPartialStateFromAction = (rs, p) => {
    switch (p[0]) {
        case Action.SetState:
            return p[1];
        case Action.ResetValueFromProps:
            return getDefaultStateFromProps(rs.props);
        case Action.UpdateSelectionPath:
            return { selectionPath: clampSelectedPath(p[1].path, rs.state.zoomLevel) };
        case Action.UpdateZoom:
            return { zoomLevel: p[1].zoom };
        case Action.SelectItem:
            return {
                zoomLevel: clamp(rs.state.zoomLevel + 1, 1, 6),
                selectionPath: clampSelectedPath(p[1].selectionPath, rs.state.zoomLevel),
            };
    }
};
const getDefaultStateFromProps = (props) => {
    const value = parseValue(props, props.value);
    return {
        value: value,
        valueRaw: getValueRaw(props, value),
        isEditing: false,
        isOpen: false,
        isFocused: false,
        zoomLevel: getDefaultZoom(props),
        selectionPath: getSelectionPath(value),
    };
};
const DateBoxInner = React.forwardRef(DateBoxComponentInner);
export const DateBox = React.forwardRef((props, ref) => (React.createElement(DateBoxInner, { ...props, ref: ref, value: Do(() => {
        if (!props.value) {
            return null;
        }
        if (props.value instanceof DateObj) {
            return props.value.datetime();
        }
        return DateTimeObj.parse(props.value, 'yyyy-MM-dd');
    }), onUpdate: v => {
        props.onUpdate(v ? v.date() : null);
    }, includeDate: true, includeTime: false })));
export const Datebox = React.forwardRef((props, ref) => (React.createElement(DateBoxInner, { ...props, ref: ref, value: Do(() => {
        if (!props.value) {
            return null;
        }
        return DateTimeObj.parse(props.value, 'yyyy-MM-dd');
    }), onUpdate: v => {
        props.onUpdate(v?.fmt() ?? null);
    }, includeDate: true, includeTime: false })));
export const DateTimeBox = React.forwardRef((props, ref) => (React.createElement(DateBoxInner, { ...props, ref: ref, value: Do(() => {
        if (!props.value) {
            return null;
        }
        if (props.value instanceof DateTimeObj) {
            return props.value;
        }
        if (props.value instanceof DateObj) {
            return props.value.datetime();
        }
        if (props.value instanceof Date) {
            return DateTimeObj.parse(props.value);
        }
        return DateTimeObj.parse(props.value, 'yyyy-MM-dd HH:mm:ss');
    }), onUpdate: v => {
        props.onUpdate(v);
    }, includeDate: true, includeTime: true })));
//# sourceMappingURL=date.js.map